// Who owns a register row (D1-c §3.2, handoff §6.2).
//
// The person tab's `Aaron (3) / All rows` filter and the Support card's
// my-tickets-only default both hang off this. It deliberately MIRRORS the
// server's `personKeys` / `assigneeMatches` in
// `server/src/handlers/standup-person-projects.ts`, because the two have to
// agree: the server decides which cards you see, the client decides which rows
// inside them are yours. If they disagree, a card appears with zero rows in it.
//
// A Jira account id is the only exact match there is. Everything else is free
// text — brain-action assignees are typed by a human ("Jay", "jay", "Jay S") —
// so the fallback is a normalised name/slug/first-name comparison.
//
// Pure functions only: no React, no fetching.

import type { RegisterRow } from "./registerRows";

/** Slug + display name + first name, lower-cased, deduped, blanks dropped. */
export function personKeys(input: { displayName?: string; personSlug?: string }): string[] {
  const slug = (input.personSlug ?? "").trim().toLowerCase();
  const display = (input.displayName ?? "").trim().toLowerCase();
  const first = display.split(/\s+/)[0] ?? "";
  return [...new Set([slug, display, first].filter(Boolean))];
}

/**
 * Does a free-text assignee field name this person? Same three-way test the
 * server uses: exact, prefixed by the key plus a space, or containing it.
 */
export function assigneeMatches(assignee: string | null | undefined, keys: string[]): boolean {
  const a = (assignee ?? "").trim().toLowerCase();
  if (!a || !keys.length) return false;
  return keys.some((k) => a === k || a.startsWith(`${k} `) || a.includes(k));
}

/** Is this register row assigned to the person whose tab we are on? */
export function rowIsTheirs(
  row: RegisterRow,
  keys: string[],
  jiraAccountId?: string,
): boolean {
  if (jiraAccountId && row.assigneeAccountId === jiraAccountId) return true;
  if (assigneeMatches(row.assignee, keys)) return true;
  // A merged row's Jira assignee can be somebody else while the brain action
  // that raised it is theirs — the action is the reason it is on their tab.
  return row.actions.some((a) => assigneeMatches(a.assignee, keys));
}

/** How many of these rows are theirs — the `Aaron (3)` count on the filter. */
export function countTheirs(
  rows: RegisterRow[],
  keys: string[],
  jiraAccountId?: string,
): number {
  return rows.filter((r) => rowIsTheirs(r, keys, jiraAccountId)).length;
}
